import { useEffect, useRef, useState } from "react"

import { logger } from "src/utils/logger"

/**
 * A hook that provides timer functionality with start, stop, and time tracking capabilities.
 * @returns An object containing:
 *   - timeLeft: The current time remaining in seconds
 *   - startTimer: Function to start the timer with a specified duration
 *   - stopTimer: Function to stop the timer and reset timeLeft to 0
 *   - isTimerRunning: Boolean indicating if the timer is running
 * @remark This timer should not be used if high precision is required. For simplicity, it uses
 * setInterval and decrements the time by 1 every tick. If precision is
 * required the timer logic in this hook should be updated to depend on a future
 * timestamp which is used to calculate the time left and to adjust for the
 * inaccuracy of when the callback in setInterval runs.
 */
export function useTimer() {
  const [timeLeft, setTimeLeft] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Starts a timer with the specified duration. If a timer is already running,
   * this function will log a message and return without starting a new timer.
   * @param duration - The duration of the timer in seconds
   * @param options - An optional object containing:
   *   - onExpire: A callback to run when the timer expires
   */
  function startTimer(duration: number, options?: { onExpire?: () => void }) {
    if (timeLeft > 0) {
      logger.info(
        `Timer is already running, call ${stopTimer.name}() before starting a new one`
      )
      return
    }

    setTimeLeft(duration)

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTimeLeft = prev - 1

        if (newTimeLeft <= 0) {
          clearTimer()
          options?.onExpire?.()
          return 0
        }

        return newTimeLeft
      })
    }, 1000)
  }

  function stopTimer() {
    setTimeLeft(0)
    clearTimer()
  }

  function clearTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }

  // Clear any timer that might be running when the component unmounts
  useEffect(() => {
    return () => {
      clearTimer()
    }
  }, [])

  return {
    timeLeft,
    startTimer,
    stopTimer,
    isTimerRunning: timeLeft > 0,
  }
}
